"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/dist/client/link";

const OurTrainingCenter: React.FC = () => {
  type SectionKey = 'rp1' | 'rp2' | 'ap' | 'ev';
  const [open, setOpen] = useState<Record<SectionKey, boolean>>({
    rp1: false,
    rp2: false,
    ap: false,
    ev: false,
  });
  const toggle = (key: SectionKey) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <section className="md:py-12 py-7 px-4 sm:bg-[#EFEFEF] bg-white md:-mt-6 -mt-10">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-red-hat text-[30px] md:text-[40px] md:leading-13.25 font-light text-[#1B365E] text-center md:mb-5.75 mb-7">
          Notre Centre de Formation
        </h2>

        {/* Competencies Card */}
        <div className="bg-white max-w-7xl mx-auto rounded-2xl sm:px-4 md:px-[70px] md:pt-[37px] sm:pt-6 md:pb-[27px] pb-7 sm:pb-6 sm:mb-[22px]">
          <div className="max-w-175 mx-auto">
            <Link href="/" className="w-full flex items-center justify-center" aria-label="Retour à l'accueil">
              <Image
                className="w-63.75 md:mx-0 mx-auto"
                src="/assets/logo-blue.svg"
                alt="Spot Formations"
                title="Spot Formations"
                width={255}
                height={17}
              />
            </Link>

            <div className="grid md:grid-cols-2 gap-x-32 gap-y-7 md:gap-y-8 mt-7 md:mt-8">
              <p className="order-1 md:text-left text-center text-sm leading-5 text-[#1B365E] pt-2">
                Numéro de SIRET <br />
                Numéro de Déclaration d’Activité (NDA)<br />
                Certification Qualiopi
              </p>

              <div className="order-2 flex gap-4.5">
                <div className="size-14 shrink-0 flex items-center justify-center border-[0.25px] border-[#B6C93E] rounded-full">
                  <Image
                    src="/assets/icons/user-card.svg"
                    alt="information-card"
                    title="Information Card"
                    width={34}
                    height={26}
                  />
                </div>
                <p className="text-sm leading-5 text-[#1B365E]">
                  <strong>Coordonnées</strong>
                  <br />
                  Adresse<br />
                  Téléphone<br />
                  Email
                </p>
              </div>

              <Image
                className="md:order-3 order-4 shrink-0 w-58.75 md:mx-0 mx-auto"
                src="/assets/qualiopi.svg"
                alt="Qualiopi"
                title="Qualiopi"
                width={235}
                height={125}
              />

              <div className="md:order-4 order-3 flex gap-4.5">
                <div className="size-14 shrink-0 flex items-center justify-center">
                  <Image
                    src="/assets/icons/wheel-chair.svg"
                    alt="Wheelchair"
                    title="Wheelchair"
                    width={33}
                    height={38}
                  />
                </div>
                <p className="text-sm leading-5 text-[#1B365E]">
                  <strong>Référent handicap</strong>
                  <br />
                  Nom<br />
                  Contact du référent handicap
                </p>
              </div>
            </div>

            <div className="flex gap-12 items-center mt-7 md:mt-10">
              <div className="flex items-center md:gap-3 gap-2.75 text-sm leading-5 font-normal text-[#1B365E]">
                <Image
                  className="md:w-6 w-[22px] md:h-[34px] h-[32px] shrink-0"
                  src="/assets/bulb.svg"
                  alt="Bulb"
                  title="Bulb"
                  width={24}
                  height={34}
                />
                <span>
                  OPCO, OPCA, CPF, aides régionales...
                  <br />
                  Optimisez le{" "}
                  <span className="underline underline-offset-2 cursor-pointer">
                    financement
                  </span>{" "}
                  de votre formation
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="relative grid grid-cols-1 lg:grid-cols-3 xl:gap-32.5 md:gap-12 gap-8 bg-white rounded-2xl md:px-[70px] sm:px-4 md:pt-[37px] sm:pt-6 md:pb-[27px] sm:pb-6">
          {/* Column 1 */}
          <div className="space-y-8">
            <div>
              <button
                type="button"
                onClick={() => toggle('rp1')}
                aria-expanded={open.rp1}
                aria-controls="section-rp1-content"
                className="bg-[#C7F8FF] w-full px-6 py-2 rounded-full text-[15px] leading-4.5 font-bold text-[#1B365E] inline-flex items-center justify-between cursor-pointer"
              >
                <span className="mx-auto md:mx-0">Ressources pédagogiques</span>
                <span className="md:hidden inline-flex shrink-0">
                  <Image className={`${open.rp1 && "rotate-90"} w-[17.46px] h-[12.34px] transition-transform 0.3s ease-in-out`} src="/assets/icons/drawer-link-arrow.svg" alt="Développer" width={17.46} height={12.34} />
                </span>
              </button>
              <div id="section-rp1-content" className={`${open.rp1 ? 'block' : 'hidden'} md:block mt-[18.46px]`}>
                <ul>
                  {[
                    "Plateforme interactive",
                    "Support de formation",
                    "Contenus en ligne mis à jour régulièrement",
                    "Suivi pendant 3 mois",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-[#1D1D1D] text-sm leading-6"
                    >
                      <span className="size-1.5 mt-[9.5px] shrink-0 rounded-full bg-black" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
                <button
                  type="button"
                  onClick={() => toggle('rp2')}
                  aria-expanded={open.rp2}
                  aria-controls="section-rp2-content"
                  className="bg-[#C7F8FF] w-full px-6 py-2 rounded-full text-[15px] leading-4.5 font-bold text-[#1B365E] inline-flex items-center justify-between cursor-pointer"
                >
                  <span className="mx-auto md:mx-0">Ressources pédagogiques</span>
                  <span className="md:hidden inline-flex shrink-0">
                      <Image className={`${open.rp2 && "rotate-90"} w-[17.46px] h-[12.34px] transition-transform 0.3s ease-in-out`} src="/assets/icons/drawer-link-arrow.svg" alt="Développer" width={17.46} height={12.34} />
                  </span>
                </button>
                <div id="section-rp2-content" className={`${open.rp2 ? 'block' : 'hidden'} md:block mt-[18.46px]`}>
                    <div className="bg-[#C7F8FF] text-[#1D1D1D] ps-4.25 pt-4.25 pb-5.25 pe-8.25 rounded-2xl relative">
                        <span className="text-[8px] block mb-3 pe-6">
                            Processus d&apos;amélioration continue chez Spot Formations
                        </span>
                        <div>
                            <div>
                                <div className="flex gap-1">
                                    <span className="text-xs leading-5">1.</span>
                                    <p className="text-xs leading-5">Recueil des appréciations</p>
                                </div>
                                <ul className="ps-5 text-[8px] leading-5">
                                    <li className="flex items-center gap-1">
                                        <span className="size-[2.4px] rounded-full bg-black" />
                                        Formulaire de contact
                                    </li>
                                </ul>
                            </div>
                            <div>
                                <div className="flex gap-1">
                                    <span className="text-xs leading-5">2.</span>
                                    <p className="text-xs leading-5">Suivi</p>
                                </div>
                                <ul className="ps-5 text-[8px] leading-5">
                                    {[
                                    "Questionnaires, Enquêtes de satisfaction",
                                    "Cadre de gestion des réclamations",
                                    ].map((item) => (
                                    <li key={item}
                                        className="flex items-center gap-1">
                                        <span className="size-[2.4px] rounded-full bg-black" />
                                        {item}
                                    </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <div className="flex gap-1">
                                    <span className="text-xs leading-5">3.</span>
                                    <p className="text-xs leading-5">Implémentation</p>
                                </div>
                                <ul className="ps-5 text-[8px] leading-5">
                                    {[
                                    "Mise à jour des formations selon les retours des apprenants",
                                    "Et l’évolution des métiers",
                                    ].map((item) => (
                                    <li key={item}
                                        className="flex items-center gap-1">
                                        <span className="size-[2.4px] rounded-full bg-black" />
                                        {item}
                                    </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="absolute top-4 right-3.25 py-2 px-3 w-[42px] h-[34px] bg-white border-b border-b-[#40BFDE] rounded-3xl flex items-center justify-center">
                            <Image className='w-4.5 shrink-0' src="/assets/icons/medal.svg" alt='Medal' title="Medal" width={18} height={18} />
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Column 2 */}
          <div>
            <button
              type="button"
              onClick={() => toggle('ap')}
              aria-expanded={open.ap}
              aria-controls="section-ap-content"
              className="bg-[#C7F8FF] w-full px-6 py-2 rounded-full text-[15px] leading-4.5 font-bold text-[#1B365E] inline-flex items-center justify-between cursor-pointer"
            >
              <span className="mx-auto md:mx-0">Approche pédagogique</span>
              <span className="md:hidden inline-flex shrink-0">
                <Image className={`${open.ap && "rotate-90"} w-[17.46px] h-[12.34px] transition-transform 0.3s ease-in-out`} src="/assets/icons/drawer-link-arrow.svg" alt="Développer" width={17.46} height={12.34} />
              </span>
            </button>
              
            <div id="section-ap-content" className={`mt-[18.46px] ${open.ap ? 'block' : 'hidden'} md:block`}>
              <ul>
                  <li className="flex items-start gap-2 text-[#1D1D1D] text-sm leading-6">
                      <span className="size-1.5 mt-[9.5px] shrink-0 rounded-full bg-black" />
                      <span>
                      Méthodologie <strong>I.A.A.</strong> <br/>
                      <strong>Impliquer -Appliquer - Accompagner</strong>
                      </span>
                  </li>
                  {[
                    "Pédagogie adaptée et personnalisée",
                    "Exercices personnalisés données par le formateur",
                    "Exercices et applications sur les plateformes",
                    "Support de formation",
                    "Echanges interactifs",
                    "Mises en situation",
                    "Modalités de suivi d’exécution de la formation, assurés par le formateur tuteur.",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-[#1D1D1D] text-sm leading-6"
                    >
                      <span className="size-1.5 mt-[9.5px] shrink-0 rounded-full bg-black" />
                      {item}
                    </li>
                  ))}
                </ul>
            </div>
          </div>

          {/* Column 3 */}
          <div>
            <button
              type="button"
              onClick={() => toggle('ev')}
              aria-expanded={open.ev}
              aria-controls="section-ev-content"
              className="bg-[#C7F8FF] w-full px-6 py-2 rounded-full text-[15px] leading-4.5 font-bold text-[#1B365E] inline-flex items-center justify-between cursor-pointer"
            >
              <span className="mx-auto md:mx-0">Évaluation et Validation</span>
              <span className="md:hidden inline-flex shrink-0">
                <Image className={`${open.ev && "rotate-90"} w-[17.46px] h-[12.34px] transition-transform 0.3s ease-in-out`} src="/assets/icons/drawer-link-arrow.svg" alt="Développer" width={17.46} height={12.34} />
              </span>
            </button>
            <div id="section-ev-content" className={`mt-[18.46px] ${open.ev ? 'block' : 'hidden'} md:block`}>
              <div>
                <p className="text-[#1D1D1D] text-sm font-bold leading-6">
                  Évaluation des compétences :
                </p>
                <ul className="mb-6 ps-2">
                  {["Tests", "Cas pratiques", "Mises en application"].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-[#1D1D1D] text-sm leading-6"
                    >
                      <span className="size-1.5 mt-[9.5px] shrink-0 rounded-full bg-black" />
                      {item}
                    </li>
                  ))}
                </ul>

                <p className="text-[#1D1D1D] text-sm font-bold leading-6">Validation :</p>
                <p className="text-[#1D1D1D] text-sm leading-6">
                  Attestation de fin de formation et attestation de présence.
                </p>
              </div>
            </div>
          </div>
            <div className="lg:absolute right-12.25 bottom-6 flex md:flex-row flex-col items-center md:gap-[26.53px]">
                <Image className="inline-flex shrink-0" src="/assets/icons/men-with-note.svg" alt="Men with Note" title="Men with Note" width={37.1} height={110.62} />
                <div className="flex flex-col gap-[9.11px]">
                    <div className="inline-flex items-center gap-2 sm:gap-3 w-fit bg-[#1B515E] rounded-lg p-2">
                        <Image className="inline-flex w-12 shrink-0" src="/assets/clicking.svg" alt="Clicking" title="Clicking" width={48} height={48} />
                        <div className="space-y-1">
                            <div className="flex gap-2">
                            {[...Array(5)].map((_, i) => (
                                <Image key={i} className="w-4 shrink-0" src="/assets/icons/star.svg" alt="Star" title="Star" width={16} height={16} />
                            ))}
                            </div>
                            <p className="text-xs text-white leading-5 tracking-[2%] ">
                            Évaluation moyenne
                            <br />
                            de nos formations
                            </p>
                        </div>
                        <Image className="shrink-0 rounded-lg" src="/assets/icons/qualiopi-small.svg" alt="Qualiopi" title="Qualiopi" width={97} height={51} />
                    </div>
                    <p className="text-[#1D1D1D] text-sm leading-6 md:text-left text-center">
                        <strong>*Taux de satisfaction de nos formations :  XXX</strong><br/>
                        *Selon sur les avis des apprenants
                    </p>
                </div>
            </div>
        </div>
      </div>
    </section>
  );
};

export default OurTrainingCenter;